import { useState, useCallback, useRef, useEffect } from 'react';
import { commentService, } from '../services/commentService';
import { eventBus } from '../events';
import { useEventSubscribe } from '../events/hooks';
import { isPermissionDenied } from '../utils/grpcErrors';
import { useAccessSettlingRetry } from './useAccessSettlingRetry';
export const useComments = ({ projectId, scopeId, limit = 50 }) => {
    const [comments, setComments] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [denied, setDenied] = useState(false);
    const [hasMore, setHasMore] = useState(false);
    const cursorRef = useRef(undefined);
    const isLoadingRef = useRef(false);
    const fetchComments = useCallback(async () => {
        if (isLoadingRef.current || !projectId)
            return;
        const sessionId = localStorage.getItem('landscope_session_id');
        const sessionToken = localStorage.getItem('landscope_session_token');
        if (!sessionId || !sessionToken)
            return;
        try {
            isLoadingRef.current = true;
            setIsLoading(true);
            setError(null);
            cursorRef.current = undefined;
            const response = await commentService.getComments({
                projectId,
                scopeId,
                limit
            });
            setComments(response.comments);
            setHasMore(response.hasMore);
            cursorRef.current = response.nextCursor;
            setDenied(false);
        }
        catch (err) {
            const message = err instanceof Error ? err.message : 'Failed to load comments';
            console.error('[useComments] Error fetching comments:', message);
            setError(message);
            setDenied(isPermissionDenied(err));
        }
        finally {
            isLoadingRef.current = false;
            setIsLoading(false);
        }
    }, [projectId, scopeId, limit]);
    useAccessSettlingRetry(projectId || null, denied, fetchComments);
    const loadMore = useCallback(async () => {
        if (isLoadingRef.current || !projectId || !hasMore || !cursorRef.current)
            return;
        const sessionId = localStorage.getItem('landscope_session_id');
        const sessionToken = localStorage.getItem('landscope_session_token');
        if (!sessionId || !sessionToken)
            return;
        try {
            isLoadingRef.current = true;
            setIsLoading(true);
            setError(null);
            const response = await commentService.getComments({
                projectId,
                scopeId,
                limit,
                cursor: cursorRef.current
            });
            setComments(prev => [...prev, ...response.comments]);
            setHasMore(response.hasMore);
            cursorRef.current = response.nextCursor;
        }
        catch (err) {
            const message = err instanceof Error ? err.message : 'Failed to load more comments';
            console.error('[useComments] Error loading more comments:', message);
            setError(message);
        }
        finally {
            isLoadingRef.current = false;
            setIsLoading(false);
        }
    }, [projectId, scopeId, limit, hasMore]);
    const createComment = useCallback(async (data) => {
        let response;
        if (data.parentCommentId) {
            response = await commentService.replyToComment({
                projectId,
                scopeId,
                parentCommentId: data.parentCommentId,
                content: data.content,
                additionalArtifactIds: data.artifactIds.length > 0 ? data.artifactIds : undefined,
                mentionedUserIds: data.mentionedUserIds
            });
        }
        else {
            response = await commentService.createComment({
                projectId,
                content: data.content,
                scopeId,
                artifactIds: data.artifactIds,
                anchor: data.anchor,
                mentionedUserIds: data.mentionedUserIds,
                tags: data.tags
            });
        }
        if (!response.success) {
            throw new Error(response.message || 'Failed to create comment');
        }
        if (data.attachments && data.attachments.length > 0) {
            const uploadPromises = data.attachments.map(file => commentService.uploadAttachment(response.commentId, file));
            await Promise.all(uploadPromises);
        }
        await fetchComments();
        eventBus.emit('comments:mutated', { projectId, kind: 'create' });
        return response.commentId;
    }, [projectId, scopeId, fetchComments]);
    const updateComment = useCallback(async (commentId, content, tags) => {
        const request = {
            commentId,
            content,
            tags
        };
        const response = await commentService.updateComment(request);
        if (!response.success) {
            throw new Error(response.message || 'Failed to update comment');
        }
        await fetchComments();
        eventBus.emit('comments:mutated', { projectId, kind: 'update' });
    }, [projectId, fetchComments]);
    const deleteComment = useCallback(async (commentId) => {
        const response = await commentService.deleteComment(commentId);
        if (!response.success) {
            throw new Error(response.message || 'Failed to delete comment');
        }
        await fetchComments();
        eventBus.emit('comments:mutated', { projectId, kind: 'delete' });
    }, [projectId, fetchComments]);
    // Refetch on cross-instance mutations so map pins, the panel list, and
    // the control-panel section stay in sync without re-mounts. The emitting
    // instance also receives its own event; fetchComments' isLoadingRef
    // guard dedupes the resulting concurrent call.
    useEventSubscribe('comments:mutated', (payload) => {
        if (payload.projectId !== projectId)
            return;
        fetchComments();
    }, [projectId, fetchComments]);
    const refetch = useCallback(async () => {
        await fetchComments();
    }, [fetchComments]);
    useEffect(() => {
        if (!projectId || comments.length === 0)
            return;
        eventBus.emit('comments:updated', {
            projectId,
            scopeId,
            comments: comments.map(c => ({
                id: c.id,
                deleted: c.deleted,
                artifactIds: c.artifactIds || [],
                replies: c.replies || []
            }))
        });
    }, [projectId, scopeId, comments]);
    return {
        comments,
        isLoading,
        error,
        hasMore,
        fetchComments,
        loadMore,
        createComment,
        updateComment,
        deleteComment,
        refetch
    };
};
